import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';

import { SHIFT_TYPES } from '../../components/UserCard/constant';
import { milesToMeters } from '../../utils/locationHelpers';
import parseQueryParamToSafeInteger from '../../utils/queryParamParsers';

import {
  DISTANCE_SEARCH_PARAM_KEYS,
  FILTER_FIELDS_TO_OPERATION,
  FILTER_MAPPING,
  ROLE_OPTIONS,
  SEARCH_PARAM_KEYS,
  SORT_MAPPING,
  STATUS_OPTIONS,
  TIME,
} from './UsersConstants';

export { getUniqObjectsByKey } from '../../utils/jobOrderHelpers';

export const DISTANCE_FILTER_FIELD = 'workerDistanceToCoordinate';

export const capitalizeFirstLetter = (string) =>
  string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();

export const buildDistanceFilter = ({ latitude, longitude, radiusMiles }) => ({
  field: DISTANCE_FILTER_FIELD,
  value: {
    latitude,
    longitude,
    maxDistance: milesToMeters(radiusMiles),
  },
});

export const removeDistanceFilter = (filters = []) =>
  filters.filter((filter) => filter.field !== DISTANCE_FILTER_FIELD);

export const upsertDistanceFilter = (filters, distanceValues) => {
  const withoutDistance = removeDistanceFilter(filters);
  if (!distanceValues?.center) return withoutDistance;

  return [
    ...withoutDistance,
    buildDistanceFilter({
      latitude: distanceValues.center.lat,
      longitude: distanceValues.center.lng,
      radiusMiles: distanceValues.radiusMiles,
    }),
  ];
};

export const getDistanceChipLabel = (distanceFilter) => {
  if (!distanceFilter) return null;

  const cityState = [distanceFilter.city, distanceFilter.state].filter(Boolean).join(', ');
  const normalizedLocationName = distanceFilter.locationName
    ?.split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(', ');
  const placeLabel = cityState || normalizedLocationName || distanceFilter.locationName;

  if (!placeLabel) return `${distanceFilter.radiusMiles} mi`;
  return `${distanceFilter.radiusMiles} mi · ${placeLabel}`;
};

export const parseDistanceFilterFromUrl = (searchParams) => {
  const latParam = searchParams.get(SEARCH_PARAM_KEYS.DISTANCE_LAT);
  const lngParam = searchParams.get(SEARCH_PARAM_KEYS.DISTANCE_LNG);
  const milesParam = searchParams.get(SEARCH_PARAM_KEYS.DISTANCE_MILES);

  if (latParam == null || lngParam == null || milesParam == null || milesParam === '') {
    return null;
  }

  const lat = Number(latParam);
  const lng = Number(lngParam);
  const radiusMiles = Number(milesParam);

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(radiusMiles)) {
    return null;
  }

  return {
    center: { lat, lng },
    radiusMiles,
    radiusType: searchParams.get(SEARCH_PARAM_KEYS.DISTANCE_RADIUS_TYPE) || 'suggested',
    locationName: searchParams.get(SEARCH_PARAM_KEYS.DISTANCE_LOCATION) || '',
    city: searchParams.get(SEARCH_PARAM_KEYS.DISTANCE_CITY) || '',
    state: searchParams.get(SEARCH_PARAM_KEYS.DISTANCE_STATE) || '',
  };
};

export const writeDistanceFilterToSearchParams = (params, distanceValues) => {
  const nextParams = { ...params };

  DISTANCE_SEARCH_PARAM_KEYS.forEach((key) => {
    delete nextParams[key];
  });

  if (!distanceValues?.center) return nextParams;

  nextParams[SEARCH_PARAM_KEYS.DISTANCE_LAT] = String(distanceValues.center.lat);
  nextParams[SEARCH_PARAM_KEYS.DISTANCE_LNG] = String(distanceValues.center.lng);
  nextParams[SEARCH_PARAM_KEYS.DISTANCE_MILES] = String(distanceValues.radiusMiles);

  if (distanceValues.radiusType) {
    nextParams[SEARCH_PARAM_KEYS.DISTANCE_RADIUS_TYPE] = distanceValues.radiusType;
  }
  if (distanceValues.city) {
    nextParams[SEARCH_PARAM_KEYS.DISTANCE_CITY] = distanceValues.city;
  }
  if (distanceValues.state) {
    nextParams[SEARCH_PARAM_KEYS.DISTANCE_STATE] = distanceValues.state;
  }
  if (distanceValues.locationName) {
    nextParams[SEARCH_PARAM_KEYS.DISTANCE_LOCATION] = distanceValues.locationName;
  }

  return nextParams;
};

const applyDistanceFilterFromUrl = (filters, searchParams) => {
  const withoutDistance = removeDistanceFilter(filters);
  const distanceValues = parseDistanceFilterFromUrl(searchParams);
  return distanceValues ? upsertDistanceFilter(withoutDistance, distanceValues) : withoutDistance;
};

const getShiftHoursFromRange = (shiftStart, shiftEnd) => {
  const startHour = Math.floor(shiftStart / 60);
  const endHourExclusive = Math.ceil(shiftEnd / 60) % 24;
  const hours = [];

  let currentHour = startHour;
  while (currentHour !== endHourExclusive) {
    hours.push(currentHour);
    currentHour = (currentHour + 1) % 24;
  }

  return hours;
};

const buildShiftFiltersFromRange = (shiftStart, shiftEnd) =>
  getShiftHoursFromRange(shiftStart, shiftEnd).map((hour) => ({
    field: `${FILTER_FIELDS_TO_OPERATION.shift.field}.${hour}`,
    operation: FILTER_FIELDS_TO_OPERATION.shift.operation,
    value: true,
  }));

export const buildFilterPayloadFromUrl = (searchParams, prevFilters) => {
  const newFilters = Object.entries(FILTER_MAPPING).reduce(
    (acc, [paramKey, { field, operation, convertValue }]) => {
      const paramValue = searchParams.get(paramKey);
      const value = convertValue ? convertValue(paramValue) : paramValue;
      const updatedFilters = acc.filter((filter) => filter.field !== field);
      return value !== null ? [...updatedFilters, { field, operation, value }] : updatedFilters;
    },
    prevFilters
  );

  const filtersWithoutShift = newFilters.filter(
    (filter) =>
      filter.field !== FILTER_FIELDS_TO_OPERATION.shift.field &&
      !filter.field.startsWith(`${FILTER_FIELDS_TO_OPERATION.shift.field}.`)
  );
  const shiftStart = parseQueryParamToSafeInteger(searchParams.get(SEARCH_PARAM_KEYS.SHIFT_START));
  const shiftEnd = parseQueryParamToSafeInteger(searchParams.get(SEARCH_PARAM_KEYS.SHIFT_END));

  if (shiftStart !== null && shiftEnd !== null) {
    return applyDistanceFilterFromUrl(
      [...filtersWithoutShift, ...buildShiftFiltersFromRange(shiftStart, shiftEnd)],
      searchParams
    );
  }

  return applyDistanceFilterFromUrl(filtersWithoutShift, searchParams);
};

export const buildSortPayloadFromUrl = (searchParams, prevSorts) => {
  const newSorts = Object.entries(SORT_MAPPING).reduce(
    (acc, [paramKey, { field, getIsDescending }]) => {
      const paramValue = searchParams.get(paramKey);

      const isDescending = getIsDescending(paramValue);
      const updatedSorts = acc.filter((filter) => filter.field !== field);
      return paramValue !== null
        ? [...updatedSorts, { field, descending: isDescending }]
        : updatedSorts;
    },
    prevSorts || []
  );

  return newSorts;
};

export const getMainHeaderKey = (searchParams, userRole) => {
  if (userRole === UserRole.RECRUITER || userRole === UserRole.EMPLOYER) {
    return 'userList.header.workers';
  }

  const valueMatch = ROLE_OPTIONS.find(
    (status) => searchParams.get(SEARCH_PARAM_KEYS.ROLE) === status.value
  );

  return valueMatch?.headerKey || 'userList.header.generic';
};

export const getSubHeaderKey = (searchParams) => {
  const valueMatch = STATUS_OPTIONS.find(
    (status) => searchParams.get(SEARCH_PARAM_KEYS.STATUS) === status.value
  );

  return valueMatch?.nameKey || 'userList.subHeader.generic';
};

export const formatTimePreference = (timeObj) => {
  switch (timeObj.type) {
    case SHIFT_TYPES.MORNING:
      return LanguageConverter('userDetail.preference.morning');
    case SHIFT_TYPES.EVENING:
      return LanguageConverter('userDetail.preference.evening');
    case SHIFT_TYPES.NIGHT:
      return LanguageConverter('userDetail.preference.night');
    case SHIFT_TYPES.CUSTOM: {
      const formatHour = (hour) => {
        const period = hour >= 12 ? TIME.PM : TIME.AM;
        const displayHour = hour % 12 || 12;
        return `${displayHour}${period}`;
      };
      return `${LanguageConverter('userDetail.preference.iPrefer')} ${formatHour(
        timeObj.start
      )} - ${formatHour(timeObj.end)}`;
    }
    default:
      return '';
  }
};

export const agencyAPICallback = (agencyId) => ({
  httpMethod: 'POST',
  route: 'agency/read',
  generateBody: (searchTerm) => {
    const filters = agencyId
      ? [{ operation: 'equalsID', field: '_id', value: agencyId }]
      : [{ operation: 'icontains', field: 'name', value: searchTerm }];
    return {
      filters,
    };
  },
});

export const recruiterAPICallback = (recruiterId) => ({
  httpMethod: 'POST',
  route: 'V2/users/read',
  generateBody: (searchTerm) => {
    const filters = recruiterId
      ? [{ operation: 'equalsID', field: '_id', value: recruiterId }]
      : [
          { operation: 'equals', field: 'role', value: 'recruiter' },
          ...(searchTerm ? [{ operation: 'icontains', field: 'name', value: searchTerm }] : []),
        ];
    return {
      filters,
    };
  },
});
